/**
 * What: Builds the Shopify study-results header actions and native product links.
 * Transport / data-flow: Validates product ID, handle, and store domain before exposing Polaris URL actions.
 *
 */
import type { ActionDescriptor } from './components/ShopifyPageActionButtons'
import { shopifyComponentCopy } from '@testfeed/product-language'

type ActionGroup<TAction> = { title: string; actions: TAction[] }

const SHOPIFY_PRODUCT_ID = /^(?:gid:\/\/shopify\/Product\/)?(\d+)$/
const SHOPIFY_VARIANT_ID = /^(?:gid:\/\/shopify\/ProductVariant\/)?(\d+)$/

/** Fails closed when a payload does not contain a canonical numeric Shopify Product GID or numeric ID. */
export function buildShopifyAdminProductUrl(productId: string | null | undefined, variantId?: string | null) {
  const match = productId?.trim().match(SHOPIFY_PRODUCT_ID)
  if (!match) return null
  const productUrl = `shopify://admin/products/${match[1]}`
  if (variantId === null || variantId === undefined) return productUrl
  const variant = variantId.trim().match(SHOPIFY_VARIANT_ID)
  return variant ? `${productUrl}/variants/${variant[1]}` : null
}

/** Composes the results header without exposing an invalid product action. */
export function buildShopifyStudyResultsActions(onNewVersion?: () => void): ActionDescriptor[] | undefined {
  return onNewVersion
    ? [{ content: shopifyComponentCopy.studySetupCard.newVersion004, onAction: onNewVersion }]
    : undefined
}

/** Places product navigation in the stable overflow menu rather than a blank Polaris URL action. */
export function buildShopifyStudyResultsProductActionGroups(
  productId: string | null | undefined,
): ActionGroup<{ content: string; url: string }>[] | undefined {
  const productUrl = buildShopifyAdminProductUrl(productId)
  return productUrl
    ? [
        {
          title: shopifyComponentCopy.resultsShopifyStudyResultsPage.moreActions031,
          actions: [
            {
              content: shopifyComponentCopy.resultsShopifyStudyResultsPage.viewProduct030,
              url: productUrl,
            },
          ],
        },
      ]
    : undefined
}
